/**
 * Sign in, and joining a school from an invitation link.
 *
 * One screen for both: an invitation arrives as `?invite=<token>`, which is the
 * only way a new colleague gets an account.
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Field, Input, PasswordInput } from '../../components/ui'
import { api, ApiError } from '../../lib/api/client'
import { useAuth } from '../../app/useAuth'
import { BrandMark } from '../../components/BrandMark'
import './signin.css'

/**
 * Deliberately the same number as ``MIN_PASSWORD_LENGTH`` in
 * app/auth/security.py. It used to be written out four times here and once
 * there, and the two sides had drifted apart - so the rule people were shown on
 * screen was not the rule the server actually enforced.
 */
const MIN_PASSWORD_LENGTH = 10

export function SignIn() {
  const [params, setParams] = useSearchParams()
  const invite = params.get('invite')
  const reset = params.get('reset')
  if (invite) return <AcceptInvite token={invite} onBack={() => setParams({})} />
  if (reset) return <ResetPassword token={reset} onBack={() => setParams({})} />
  return <SignInForm />
}

function Hero() {
  return (
    <div className="tc-signin-hero">
      <BrandMark size={36} />
      <div>
        <div className="tc-signin-name">
          TeachCraft<span className="tc-brand-ai">AI</span>
        </div>
        <div className="tc-signin-tag">Worksheets and exam papers, ready to print.</div>
      </div>
    </div>
  )
}

function SignInForm() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [forgotten, setForgotten] = useState(false)

  if (forgotten) {
    return <ForgotPassword initialEmail={email} onBack={() => setForgotten(false)} />
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await signIn(email, password)
    } catch (problem) {
      // The server writes this message for a teacher, and says the same thing
      // whether or not the account exists. Showing it unchanged is deliberate.
      setError(problem instanceof ApiError ? problem.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="tc-signin">
      <form className="tc-signin-card" onSubmit={submit}>
        <Hero />
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@school.com"
          />
        </Field>
        <Field label="Password">
          <PasswordInput
            
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && (
          <p className="tc-signin-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <button
          className="tc-signin-link"
          type="button"
          onClick={() => setForgotten(true)}
        >
          Forgot password?
        </button>
        <p className="tc-signin-foot">
          Accounts are created by your school administrator. Ask them if you cannot get in.
        </p>
      </form>
    </main>
  )
}

function ForgotPassword({
  initialEmail,
  onBack,
}: {
  initialEmail: string
  onBack: () => void
}) {
  const [email, setEmail] = useState(initialEmail)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const response = await api.forgotPassword(email)
      setMessage(response.message)
    } catch (problem) {
      setError(
        problem instanceof ApiError
          ? problem.message
          : 'Could not request a password reset. Try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="tc-signin">
      <form className="tc-signin-card" onSubmit={submit}>
        <Hero />
        <div className="tc-signin-heading">
          <h2>Reset your password</h2>
          <p>Enter the email address used for your school account.</p>
        </div>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@school.com"
          />
        </Field>
        {message && (
          <p className="tc-signin-success" role="status">
            {message}
          </p>
        )}
        {error && (
          <p className="tc-signin-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy || Boolean(message)}>
          {busy ? 'Checking…' : message ? 'Request received' : 'Send reset link'}
        </Button>
        <button className="tc-signin-link" type="button" onClick={onBack}>
          Back to sign in
        </button>
      </form>
    </main>
  )
}

function ResetPassword({ token, onBack }: { token: string; onBack: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [complete, setComplete] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirm) {
      setError('The two passwords do not match. Type them both again.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.resetPassword(token, password)
      setComplete(true)
    } catch (problem) {
      setError(
        problem instanceof ApiError
          ? problem.message
          : 'Could not reset your password. Request a new link.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="tc-signin">
      <form className="tc-signin-card" onSubmit={submit}>
        <Hero />
        <div className="tc-signin-heading">
          <h2>{complete ? 'Password changed' : 'Choose a new password'}</h2>
          <p>
            {complete
              ? 'You can now sign in with your new password.'
              : `Use at least ${MIN_PASSWORD_LENGTH} characters.`}
          </p>
        </div>
        {!complete && (
          <>
            <Field label="New password">
              <PasswordInput
                
                autoComplete="new-password"
                autoFocus
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label="Confirm new password">
              <PasswordInput
                
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </Field>
          </>
        )}
        {complete && (
          <p className="tc-signin-success" role="status">
            Your password was updated successfully.
          </p>
        )}
        {error && (
          <p className="tc-signin-error" role="alert">
            {error}
          </p>
        )}
        {!complete && (
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        )}
        <button className="tc-signin-link" type="button" onClick={onBack}>
          {complete ? 'Go to sign in' : 'Cancel'}
        </button>
      </form>
    </main>
  )
}

function AcceptInvite({ token, onBack }: { token: string; onBack: () => void }) {
  const { acceptInvite } = useAuth()
  const [preview, setPreview] = useState<{
    full_name: string
    email: string
    roles: string[]
    school_name: string
    expired: boolean
  } | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .invitePreview(token)
      .then(setPreview)
      .catch((problem) => {
        setPreviewError(
          problem instanceof ApiError
            ? problem.message
            : 'That invitation link could not be verified.',
        )
      })
      .finally(() => setLoading(false))
  }, [token])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirm) {
      setError('The two passwords do not match. Type them both again.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await acceptInvite(token, preview?.full_name ?? '', password)
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not create your account.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <main className="tc-signin">
        <div className="tc-signin-card">
          <Hero />
          <p style={{ textAlign: 'center', color: 'var(--tc-muted)' }}>Verifying your invitation…</p>
        </div>
      </main>
    )
  }

  if (previewError || preview?.expired) {
    return (
      <main className="tc-signin">
        <div className="tc-signin-card">
          <Hero />
          <div className="tc-signin-heading">
            <h2>Invitation unavailable</h2>
            <p>
              {preview?.expired
                ? 'This invitation has expired. Ask your administrator to send a new one.'
                : previewError}
            </p>
          </div>
          <button className="tc-signin-link" type="button" onClick={onBack}>
            Back to sign in
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className="tc-signin">
      <form className="tc-signin-card" onSubmit={submit}>
        <Hero />
        <div className="tc-signin-heading">
          <h2>Join {preview?.school_name ?? 'your school'}</h2>
          <p>Set a password to activate your account.</p>
        </div>

        <div className="tc-invite-context">
          <div className="tc-invite-context-row">
            <span className="tc-invite-context-label">Name</span>
            <span>{preview?.full_name}</span>
          </div>
          <div className="tc-invite-context-row">
            <span className="tc-invite-context-label">Email</span>
            <span>{preview?.email}</span>
          </div>
          <div className="tc-invite-context-row">
            <span className="tc-invite-context-label">Role</span>
            <span>{(preview?.roles ?? []).map(roleLabel).join(', ')}</span>
          </div>
        </div>

        <Field label="Choose a password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
          <PasswordInput
            
            autoComplete="new-password"
            autoFocus
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Confirm password">
          <PasswordInput
            
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {error && (
          <p className="tc-signin-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create my account'}
        </Button>
        <button className="tc-signin-link" type="button" onClick={onBack}>
          Back to sign in
        </button>
      </form>
    </main>
  )
}

function roleLabel(role: string) {
  const named: Record<string, string> = {
    school_admin: 'School administrator',
    print_admin: 'Printing',
    teacher: 'Teacher',
    reviewer: 'Reviewer',
    admin: 'Site administrator',
  }
  return named[role] ?? role.charAt(0).toUpperCase() + role.slice(1)
}
